import { FormEvent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Images } from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Highlight from '@tiptap/extension-highlight';
import { TextStyle } from '@tiptap/extension-text-style';
import type { NewsArticle, NewsCategory } from '@tsuyosa/types';
import { useAuth } from '../context/AuthContext';
import { getAdminNews, createNews, updateNews } from '../lib/api';
import { Spinner, Button } from '@tsuyosa/ui';
import ImagePickerModal from '../components/ImagePickerModal';

const CATEGORIES: NewsCategory[] = [
  'BIRTHDAY',
  'NEW MEMBER',
  'EVENT',
  'GRADUATION',
  'RELEASE',
  'CONCERT',
  'ANNOUNCEMENT',
];

const DEFAULT_FORM: Partial<NewsArticle> = {
  title: '',
  slug: '',
  date: new Date().toISOString().split('T')[0],
  category: undefined,
  text: '',
  content: '',
  image_url: '',
  is_featured: false,
  is_draft: true,
};

function slugify(str: string) {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

export default function NewsFormPage() {
  const { id } = useParams<{ id?: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [form, setForm] = useState<Partial<NewsArticle>>(DEFAULT_FORM);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(isEdit);
  const [error, setError] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);

  const editor = useEditor({
    extensions: [StarterKit, Underline, Link, Image, Highlight, TextStyle],
    content: form.content ?? '',
    onUpdate: ({ editor }) => {
      setForm((prev) => ({ ...prev, content: editor.getHTML() }));
    },
  });

  useEffect(() => {
    if (!isEdit || !accessToken || !id) return;
    getAdminNews(accessToken)
      .then(({ data }) => {
        const found = data.find((a) => a.id === id);
        if (found) {
          setForm(found);
          editor?.commands.setContent(found.content ?? '');
        }
      })
      .finally(() => setIsFetching(false));
  }, [isEdit, accessToken, id, editor]);

  function handleTitleChange(title: string) {
    setForm((prev) => ({
      ...prev,
      title,
      slug: isEdit ? prev.slug : slugify(title),
    }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!accessToken) return;
    setError('');
    setIsLoading(true);
    try {
      const payload = { ...form, content: editor?.getHTML() ?? form.content };
      if (isEdit && id) {
        await updateNews(accessToken, id, payload);
      } else {
        await createNews(accessToken, payload);
      }
      navigate('/news');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsLoading(false);
    }
  }

  if (isFetching) {
    return (
      <div className="flex justify-center py-20">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-6 flex items-center gap-3">
        <button
          onClick={() => navigate('/news')}
          className="rounded-lg p-1.5 text-gray-400 hover:bg-white/5 hover:text-gray-200"
        >
          <ArrowLeft size={18} />
        </button>
        <h1 className="text-xl font-semibold text-gray-100">
          {isEdit ? 'Edit Article' : 'Create Article'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Title */}
        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-400">
            Title <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            required
            value={form.title ?? ''}
            onChange={(e) => handleTitleChange(e.target.value)}
            className="w-full rounded-lg border border-[var(--color-border)] bg-black/20 px-3 py-2 text-sm text-gray-200 placeholder:text-gray-600 focus:border-pink-500/50 focus:outline-none"
          />
        </div>

        {/* Slug */}
        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-400">Slug</label>
          <input
            type="text"
            value={form.slug ?? ''}
            onChange={(e) => setForm((prev) => ({ ...prev, slug: slugify(e.target.value) }))}
            className="w-full rounded-lg border border-[var(--color-border)] bg-black/20 px-3 py-2 font-mono text-xs text-gray-400 focus:border-pink-500/50 focus:outline-none"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Date */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-gray-400">Date</label>
            <input
              type="date"
              required
              value={form.date ?? ''}
              onChange={(e) => setForm((prev) => ({ ...prev, date: e.target.value }))}
              className="w-full rounded-lg border border-[var(--color-border)] bg-black/20 px-3 py-2 text-sm text-gray-200 focus:border-pink-500/50 focus:outline-none [color-scheme:dark]"
            />
          </div>

          {/* Category */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-gray-400">Category</label>
            <select
              value={form.category ?? ''}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  category: (e.target.value as NewsCategory) || undefined,
                }))
              }
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-gray-200 focus:border-pink-500/50 focus:outline-none"
            >
              <option value="">No category</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Excerpt / text */}
        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-400">
            Excerpt / Summary
          </label>
          <textarea
            rows={2}
            value={form.text ?? ''}
            onChange={(e) => setForm((prev) => ({ ...prev, text: e.target.value }))}
            placeholder="Short description shown in listings…"
            className="w-full rounded-lg border border-[var(--color-border)] bg-black/20 px-3 py-2 text-sm text-gray-200 placeholder:text-gray-600 focus:border-pink-500/50 focus:outline-none resize-none"
          />
        </div>

        {/* Cover image */}
        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-400">Cover Image</label>
          <div className="flex gap-3">
            {form.image_url && (
              <img src={form.image_url} alt="cover preview" className="h-20 w-32 rounded-lg object-cover" />
            )}
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              className="flex cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-[var(--color-border)] px-4 py-3 text-xs text-gray-500 hover:border-pink-500/30 hover:text-pink-400 transition-colors"
            >
              <Images size={16} />
              Pilih Gambar
            </button>
          </div>
        </div>

        {/* TipTap editor */}
        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-400">Content</label>

          {/* Toolbar */}
          <div className="flex flex-wrap gap-1 rounded-t-lg border border-b-0 border-[var(--color-border)] bg-[var(--color-surface)] p-2">
            {[
              { label: 'B', cmd: () => editor?.chain().focus().toggleBold().run(), active: editor?.isActive('bold') },
              { label: 'I', cmd: () => editor?.chain().focus().toggleItalic().run(), active: editor?.isActive('italic') },
              { label: 'U', cmd: () => editor?.chain().focus().toggleUnderline().run(), active: editor?.isActive('underline') },
              { label: 'H2', cmd: () => editor?.chain().focus().toggleHeading({ level: 2 }).run(), active: editor?.isActive('heading', { level: 2 }) },
              { label: 'H3', cmd: () => editor?.chain().focus().toggleHeading({ level: 3 }).run(), active: editor?.isActive('heading', { level: 3 }) },
              { label: 'UL', cmd: () => editor?.chain().focus().toggleBulletList().run(), active: editor?.isActive('bulletList') },
              { label: 'OL', cmd: () => editor?.chain().focus().toggleOrderedList().run(), active: editor?.isActive('orderedList') },
              { label: '"', cmd: () => editor?.chain().focus().toggleBlockquote().run(), active: editor?.isActive('blockquote') },
            ].map((btn) => (
              <button
                key={btn.label}
                type="button"
                onClick={btn.cmd}
                className={`rounded px-2 py-1 text-xs font-medium transition-colors ${
                  btn.active
                    ? 'bg-pink-500 text-white'
                    : 'text-gray-400 hover:bg-white/10 hover:text-gray-200'
                }`}
              >
                {btn.label}
              </button>
            ))}
          </div>

          <div className="rounded-b-lg border border-[var(--color-border)] bg-black/20">
            <EditorContent editor={editor} />
          </div>
        </div>

        {/* Toggles */}
        <div className="flex flex-wrap gap-5">
          <label className="flex cursor-pointer items-center gap-2.5">
            <input
              type="checkbox"
              checked={form.is_featured ?? false}
              onChange={(e) => setForm((prev) => ({ ...prev, is_featured: e.target.checked }))}
              className="h-4 w-4 rounded accent-pink-500"
            />
            <span className="text-sm text-gray-300">Featured article</span>
          </label>
          <label className="flex cursor-pointer items-center gap-2.5">
            <input
              type="checkbox"
              checked={form.is_draft ?? true}
              onChange={(e) => setForm((prev) => ({ ...prev, is_draft: e.target.checked }))}
              className="h-4 w-4 rounded accent-pink-500"
            />
            <span className="text-sm text-gray-300">Save as draft</span>
          </label>
        </div>

        {error && (
          <p className="rounded-lg bg-red-500/10 px-3 py-2 text-xs text-red-400">{error}</p>
        )}

        <div className="flex gap-3 pt-2">
          <Button type="button" variant="outline" size="sm" onClick={() => navigate('/news')}>
            Cancel
          </Button>
          <Button type="submit" variant="tsuyosa" size="sm" isLoading={isLoading}>
            {isEdit ? 'Save Changes' : 'Create Article'}
          </Button>
        </div>
        <ImagePickerModal
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onSelect={(url) => setForm((prev) => ({ ...prev, image_url: url }))}
          defaultFolder="news"
        />
      </form>
    </div>
  );
}
